const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:7001";

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

class ApiClient {
  private baseUrl: string;
  private token: string | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    // Try to restore token from localStorage
    this.token = localStorage.getItem("lab_token");
  }

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem("lab_token", token);
    } else {
      localStorage.removeItem("lab_token");
    }
  }

  getToken(): string | null {
    return this.token;
  }

  private async request<T>(
    method: string,
    endpoint: string,
    body?: unknown,
    params?: Record<string, string | number | boolean | undefined>,
    extraHeaders?: Record<string, string>,
  ): Promise<T> {
    const url = new URL(`/v1${endpoint}`, this.baseUrl);

    // Add query params
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          url.searchParams.set(key, String(value));
        }
      });
    }

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
    }
    Object.assign(headers, extraHeaders);

    const response = await fetch(url.toString(), {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    // Parse only when there IS a body. A 204 (announcements DELETE was the
    // first to hit this) has none, and an unconditional response.json() threw
    // on the SUCCESS path — the server had already done the work, the client
    // then reported failure, and retrying "failed" identically while quietly
    // succeeding every time.
    const text = await response.text();
    const data = text ? JSON.parse(text) : undefined;

    if (!response.ok) {
      throw new ApiError(
        response.status,
        data?.error?.code || "UNKNOWN_ERROR",
        data?.error?.message || "An error occurred",
      );
    }

    return data as T;
  }

  async get<T>(
    endpoint: string,
    params?: Record<string, string | number | boolean | undefined>,
  ): Promise<T> {
    return this.request<T>("GET", endpoint, undefined, params);
  }

  /** `headers` is for the few calls that carry one, like the dev key. */
  async post<T>(
    endpoint: string,
    body?: unknown,
    options?: { headers?: Record<string, string> },
  ): Promise<T> {
    return this.request<T>("POST", endpoint, body, undefined, options?.headers);
  }

  async put<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>("PUT", endpoint, body);
  }

  async patch<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>("PATCH", endpoint, body);
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>("DELETE", endpoint);
  }

  /** A file's bytes, fetched with the token: for a file whose address needs one. */
  async getBlob(endpoint: string): Promise<Blob> {
    const headers: Record<string, string> = {};
    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
    }
    const response = await fetch(new URL(`/v1${endpoint}`, this.baseUrl).toString(), { headers });
    if (!response.ok) {
      const text = await response.text();
      const data = text ? JSON.parse(text) : undefined;
      throw new ApiError(
        response.status,
        data?.error?.code || "UNKNOWN_ERROR",
        data?.error?.message || "An error occurred",
      );
    }
    return response.blob();
  }

  /**
   * Upload a file using FormData
   */
  async uploadFile<T>(
    endpoint: string,
    file: File,
    additionalData?: Record<string, string>,
  ): Promise<T> {
    const url = new URL(`/v1${endpoint}`, this.baseUrl);

    const headers: Record<string, string> = {};
    // Don't set Content-Type - browser will set it with boundary for multipart/form-data
    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
    }

    const formData = new FormData();
    formData.append("file", file);

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    const response = await fetch(url.toString(), {
      method: "POST",
      headers,
      body: formData,
    });

    // Same empty-body guard as request() — an error response with no JSON
    // (a gateway 502, say) must surface its status, not a parse crash.
    const text = await response.text();
    const data = text ? JSON.parse(text) : undefined;

    if (!response.ok) {
      throw new ApiError(
        response.status,
        data?.error?.code || "UNKNOWN_ERROR",
        data?.error?.message || "An error occurred",
      );
    }

    return data as T;
  }
}

export const api = new ApiClient(API_BASE_URL);
